import type { OperationsIntakeItemDto, OperationsItemDto, OperationsItemsFacetsDto, OperationsItemsSortKey, OperationsFacetCountDto } from "@/api/endpoints";
import { ATTENTION_ORDER, START_RISK_ORDER } from "./operationsLabels";
import { effectiveSort, type DrawerView } from "./drawerView";

/** A row of the owner, team and division desk views (2026-10-27): a request, or an intake the owner holds. */
export type DeskRow = { kind: "request"; item: OperationsItemDto } | { kind: "intake"; item: OperationsIntakeItemDto };

/** The Engagement type an intake row answers to: none is recorded on an intake. */
export const INTAKE_ENGAGEMENT = "Not set";

/** The Pending-with facet key an intake row answers to: the work order while it waits for its request, else none. */
export function intakePendingKey(item: OperationsIntakeItemDto): string {
  return item.journeyState === "awaitingRequest" ? "WorkOrder" : "None";
}

/** The drawer's chips and dropdowns, applied to an intake row the API did not filter (it only took the bucket and the search). */
export function intakeMatchesView(item: OperationsIntakeItemDto, view: DrawerView): boolean {
  if (view.attention.length > 0 && !view.attention.includes(item.attention)) return false;
  if (view.startRisk.length > 0 && !view.startRisk.includes(item.startRisk)) return false;
  if (view.pendingWith.length > 0 && !view.pendingWith.includes(intakePendingKey(item))) return false;
  if (view.workType.length > 0 && !view.workType.includes(item.workType)) return false;
  if (view.owner.length > 0 && !view.owner.includes(item.pendingWith ?? "Unassigned")) return false;
  // An intake carries no engagement type: it reads "Not set" (2026-10-28).
  if (view.engagement.length > 0 && !view.engagement.includes(INTAKE_ENGAGEMENT)) return false;
  if (view.holder.length > 0 && !view.holder.includes(item.holder)) return false;
  return true;
}

function rank<T extends string>(order: readonly T[], value: string): number {
  const i = order.indexOf(value as T);
  return i < 0 ? order.length : i;
}

/** A comparable value per sort key, the same reading for both kinds of row. */
function sortValue(row: DeskRow, key: OperationsItemsSortKey): number | string {
  if (row.kind === "request") {
    const i = row.item;
    switch (key) {
      case "worker": return (i.request.workerName ?? "").toLowerCase();
      case "client": return (i.request.client ?? "").toLowerCase();
      case "type": return i.workType.toLowerCase();
      case "stage": return i.stage ?? "";
      case "age": return i.ageBd;
      case "pendingFor": return i.pendingForBd ?? -1;
      case "attention": return ATTENTION_ORDER.length - rank(ATTENTION_ORDER, i.attention[0] ?? "clear");
      case "pendingWith": return i.waitingOn?.role ?? (i.waitingOn?.kind === "workOrder" ? "WorkOrder" : "");
      case "owner": return i.owner.toLowerCase();
      case "startLead": return i.startLeadBd ?? Number.MAX_SAFE_INTEGER;
    }
  }
  const i = row.item;
  switch (key) {
    case "worker": return (i.worker ?? "").toLowerCase();
    case "client": return (i.client ?? "").toLowerCase();
    case "type": return i.workType.toLowerCase();
    case "stage": return i.journeyState === "awaitingRequest" ? "awaitingRequest" : "intake";
    case "age": return i.waitingBd;
    case "pendingFor": return i.sinceStatusBd;
    case "attention": return ATTENTION_ORDER.length - rank(ATTENTION_ORDER, i.attention);
    case "pendingWith": return intakePendingKey(i);
    case "owner": return (i.pendingWith ?? "Unassigned").toLowerCase();
    case "startLead": return i.startLeadBd ?? Number.MAX_SAFE_INTEGER;
  }
}

/**
 * The request rows as the API sorted them, with the owner's intakes slotted in by the same sort: each intake goes
 * before the first request it should precede, so the requests' own order never changes.
 */
export function deskRows(requests: OperationsItemDto[], intakes: OperationsIntakeItemDto[], view: DrawerView): DeskRow[] {
  const rows: DeskRow[] = requests.map((item) => ({ kind: "request", item }));
  const held = intakes.filter((i) => intakeMatchesView(i, view));
  if (held.length === 0) return rows;
  const { sort, desc } = effectiveSort(view);
  const before = (a: DeskRow, b: DeskRow) => {
    const va = sortValue(a, sort);
    const vb = sortValue(b, sort);
    if (va === vb) return false;
    return desc ? va > vb : va < vb;
  };
  for (const item of held) {
    const row: DeskRow = { kind: "intake", item };
    let at = rows.findIndex((r) => r.kind === "request" && before(row, r));
    if (at < 0) at = rows.length;
    rows.splice(at, 0, row);
  }
  return rows;
}

function bump(values: OperationsFacetCountDto[] | undefined, key: string): OperationsFacetCountDto[] | undefined {
  if (!values) return values;
  const hit = values.find((v) => v.value === key);
  return hit ? values.map((v) => (v === hit ? { ...v, count: v.count + 1 } : v)) : [...values, { value: key, count: 1 }];
}

/** The chip and dropdown counts with the held intakes added, so the bar describes the whole list. */
export function mergeFacets(facets: OperationsItemsFacetsDto | undefined, intakes: OperationsIntakeItemDto[]): OperationsItemsFacetsDto | undefined {
  if (!facets || intakes.length === 0) return facets;
  let next = facets;
  for (const i of intakes) {
    next = {
      ...next,
      attention: bump(next.attention, i.attention) ?? next.attention,
      startRisk: bump(next.startRisk, i.startRisk) ?? next.startRisk,
      pendingWith: bump(next.pendingWith, intakePendingKey(i)) ?? next.pendingWith,
      workType: bump(next.workType, i.workType) ?? next.workType,
      owner: bump(next.owner, i.pendingWith ?? "Unassigned") ?? next.owner,
      engagement: bump(next.engagement, INTAKE_ENGAGEMENT) ?? next.engagement,
      holder: bump(next.holder, i.holder) ?? next.holder,
    };
  }
  // Start date chips keep their fixed order (breached, atRisk, onTime, none).
  next = { ...next, startRisk: [...next.startRisk].sort((a, b) => rank(START_RISK_ORDER, a.value) - rank(START_RISK_ORDER, b.value)) };
  return next;
}
